/**
 * 1.8.0 rights management: the pure rules both streams share (docs/dev/rights-1.8.0-build.md, plan section 3–6).
 * Step 0's file: streams A and B read it and send changes to the merger (new exports only).
 *
 * An asset's rights come from metadata fields that carry a rights role (metadata.ts RIGHTS_ROLES): a status, the
 * holder, the source, a reference, the dates it may be used from and until, restrictions and a credit. rightsState
 * turns those values into one state, strongest first:
 *
 *   withdrawn > ended > embargoed > not_cleared > restricted > unknown > cleared
 *
 *  - withdrawn: the status says so (a kill order); nothing overrides it;
 *  - ended: Available until has passed (the rights end at the end of that day in the workspace time zone);
 *  - embargoed: before Available from;
 *  - not_cleared and restricted: the status;
 *  - unknown: no status;
 *  - cleared: the status, inside its dates.
 *
 * The administrator's policy (RightsPolicy) says what each state does when material leaves PRISM: block, block
 * unless a rights manager overrides with a reason, warn (the user confirms), or allow. Withdrawn always blocks.
 */
import { z } from 'zod';
import { DEFAULT_SCHEDULE_TIMEZONE, isTimeZone, localDate, normalizeDateTime, parseDateValue, shiftDate, zonedTimeToUtc } from './date-schedule.ts';
import { ticksToTimecodeString, type Rational } from './time.ts';

const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

// ---------------------------------------------------------------- statuses, roles and states

/** The fixed values of the Rights status vocabulary (their labels are editable in the model). */
export const RIGHTS_STATUSES = ['cleared', 'restricted', 'not_cleared', 'withdrawn'] as const;
export type RightsStatus = (typeof RIGHTS_STATUSES)[number];
export const RIGHTS_STATUS_LABELS: Record<RightsStatus, string> = {
  cleared: 'Cleared', restricted: 'Restricted', not_cleared: 'Not cleared', withdrawn: 'Withdrawn',
};

/** The metadata field roles of rights fields (FieldDefinition.role, metadata.ts). */
export const RIGHTS_ROLES = [
  'rights_status', 'rights_holder', 'rights_source', 'rights_reference', 'rights_from', 'rights_until', 'rights_restrictions', 'rights_credit',
] as const;
export type RightsRole = (typeof RIGHTS_ROLES)[number];

/** An asset's raw rights values by role (metadata.ts rightsValues): what the fields hold, unchecked. */
export type RightsValues = Partial<Record<RightsRole, unknown>>;

/** Every state, strongest first: combineRights and the policy order by this. */
export const RIGHTS_STATES = ['withdrawn', 'ended', 'embargoed', 'not_cleared', 'restricted', 'unknown', 'cleared'] as const;
export type RightsState = (typeof RIGHTS_STATES)[number];
export const RIGHTS_STATE_LABELS: Record<RightsState, string> = {
  withdrawn: 'Withdrawn', ended: 'Rights ended', embargoed: 'Under embargo', not_cleared: 'Not cleared', restricted: 'Restricted',
  unknown: 'No rights information', cleared: 'Cleared',
};

/**
 * The traffic light of a state: red for withdrawn, ended, embargoed and not cleared; amber for restricted (and for a
 * cleared asset whose rights end soon: rightsColor); green for cleared; none for unknown (no mark is shown).
 */
export type RightsColor = 'red' | 'amber' | 'green' | 'none';
export const RIGHTS_STATE_COLORS: Record<RightsState, RightsColor> = {
  withdrawn: 'red', ended: 'red', embargoed: 'red', not_cleared: 'red', restricted: 'amber', unknown: 'none', cleared: 'green',
};

/** The colour of a state, with ending soon turning a green one amber. */
export function rightsColor(r: { state: RightsState; endingSoon?: boolean }): RightsColor {
  const c = RIGHTS_STATE_COLORS[r.state];
  return c === 'green' && r.endingSoon ? 'amber' : c;
}

/** 0 for the strongest (withdrawn) … 6 for cleared. */
export const rightsRank = (s: RightsState): number => RIGHTS_STATES.indexOf(s);

/** The stronger (more restrictive) of two states. */
export const strongerRights = (a: RightsState, b: RightsState): RightsState => (rightsRank(a) <= rightsRank(b) ? a : b);

export const isRightsStatus = (v: unknown): v is RightsStatus => typeof v === 'string' && (RIGHTS_STATUSES as readonly string[]).includes(v);

// ---------------------------------------------------------------- one asset's state

/** An asset's rights worked out at a moment (rightsState). */
export interface RightsInfo {
  state: RightsState;
  /** The explicit status, or null when none is set (or it is not one of the four). */
  status: RightsStatus | null;
  /** The rights end within the ending-soon days (not set once they have ended). */
  endingSoon: boolean;
  /** Available from, as a UTC instant (`2026-10-20T15:00:00Z`); null when not set. */
  from: string | null;
  /** Available until, as a date (`YYYY-MM-DD`); the rights end at the end of that day in the workspace time zone. */
  until: string | null;
  holder: string | null;
  source: string | null;
  reference: string | null;
  restrictions: string | null;
  credit: string | null;
}

const text = (v: unknown): string | null => {
  if (typeof v === 'string') return v.trim() || null;
  if (Array.isArray(v)) return v.filter((x) => typeof x === 'string' && x.trim()).map((x: string) => x.trim()).join(', ') || null;
  return null;
};

/** A date value (`YYYY-MM-DD`), or the local date of a date-time value in `tz`; null for anything else. */
function untilDate(v: unknown, tz: string): string | null {
  const p = parseDateValue(v);
  if (!p) return null;
  return p.kind === 'date' ? p.date : localDate(p.ms, tz);
}

/** A date-time value as an instant; a bare date is its start of day in `tz`. Null for anything else. */
function fromInstant(v: unknown, tz: string): number | null {
  const p = parseDateValue(v);
  if (!p) return null;
  if (p.kind === 'datetime') return p.ms;
  const [y, mo, d] = p.date.split('-').map(Number) as [number, number, number];
  return zonedTimeToUtc({ y, mo, d, h: 0, mi: 0, s: 0 }, tz);
}

/** When rights that run until `date` end: the start of the next day in `tz` (epoch milliseconds). */
export function rightsEndMs(date: string, tz: string): number {
  const [y, mo, d] = shiftDate(date, 1).split('-').map(Number) as [number, number, number];
  return zonedTimeToUtc({ y, mo, d, h: 0, mi: 0, s: 0 }, tz);
}

/**
 * The rights of one asset from its rights values (the role-mapped fields, own value first: metadata.ts
 * rightsValues) at `now` (epoch milliseconds), with dates read in `tz`. `endingSoonDays` is the workspace's setting.
 */
export function rightsState(values: RightsValues, now: number, tz: string, endingSoonDays = DEFAULT_ENDING_SOON_DAYS): RightsInfo {
  const status = isRightsStatus(values.rights_status) ? values.rights_status : null;
  const fromMs = fromInstant(values.rights_from, tz);
  const until = untilDate(values.rights_until, tz);
  const endMs = until ? rightsEndMs(until, tz) : null;
  const ended = endMs !== null && now >= endMs;
  const state: RightsState = status === 'withdrawn' ? 'withdrawn'
    : ended ? 'ended'
    : fromMs !== null && now < fromMs ? 'embargoed'
    : status === 'not_cleared' ? 'not_cleared'
    : status === 'restricted' ? 'restricted'
    : status === null ? 'unknown'
    : 'cleared';
  return {
    state, status,
    endingSoon: endMs !== null && !ended && endMs - now <= endingSoonDays * DAY_MS,
    from: fromMs === null ? null : normalizeDateTime(new Date(fromMs).toISOString()),
    until,
    holder: text(values.rights_holder),
    source: text(values.rights_source),
    reference: text(values.rights_reference),
    restrictions: text(values.rights_restrictions),
    credit: text(values.rights_credit),
  };
}

// ---------------------------------------------------------------- several sources together

/** Several rights together (a sequence's sources, or an asset with its genealogy): combineRights. */
export interface CombinedRights {
  /** The strongest state; unknown for an empty list. */
  state: RightsState;
  /** Any of them ends soon. */
  endingSoon: boolean;
  /** The latest Available from (the embargo that lifts last). */
  from: string | null;
  /** The earliest Available until. */
  until: string | null;
  /** Every restriction and credit once, in the order first met. */
  restrictions: string[];
  credits: string[];
  /** How many of them are in each state. */
  counts: Record<RightsState, number>;
}

export const emptyRightsCounts = (): Record<RightsState, number> => Object.fromEntries(RIGHTS_STATES.map((s) => [s, 0])) as Record<RightsState, number>;

/** The most restrictive state wins; the latest from and the earliest until; restrictions and credits without duplicates. */
export function combineRights(list: ReadonlyArray<Pick<RightsInfo, 'state' | 'endingSoon' | 'from' | 'until' | 'restrictions' | 'credit'>>): CombinedRights {
  const counts = emptyRightsCounts();
  let state: RightsState | null = null;
  let from: string | null = null;
  let until: string | null = null;
  const restrictions: string[] = [];
  const credits: string[] = [];
  for (const r of list) {
    counts[r.state]++;
    state = state ? strongerRights(state, r.state) : r.state;
    if (r.from && (!from || Date.parse(r.from) > Date.parse(from))) from = r.from;
    if (r.until && (!until || r.until < until)) until = r.until;
    if (r.restrictions && !restrictions.includes(r.restrictions)) restrictions.push(r.restrictions);
    if (r.credit && !credits.includes(r.credit)) credits.push(r.credit);
  }
  return { state: state ?? 'unknown', endingSoon: list.some((r) => r.endingSoon), from, until, restrictions, credits, counts };
}

// ---------------------------------------------------------------- the short form on DTOs

/**
 * The rights put on asset summaries and details, the editor's sources, export rows and MCP results (absent while
 * rights management is off). `fromSources`: the state includes the sources the asset was made from (its genealogy).
 */
export interface RightsSummary {
  state: RightsState;
  endingSoon: boolean;
  status: RightsStatus | null;
  from: string | null;
  until: string | null;
  holder: string | null;
  source: string | null;
  restrictions: string | null;
  credit: string | null;
  fromSources?: boolean;
}

export function rightsSummaryOf(r: RightsInfo, fromSources?: boolean): RightsSummary {
  return {
    state: r.state, endingSoon: r.endingSoon, status: r.status, from: r.from, until: r.until, holder: r.holder, source: r.source,
    restrictions: r.restrictions, credit: r.credit, ...(fromSources ? { fromSources: true } : {}),
  };
}

// ---------------------------------------------------------------- the policy and the settings

/** What a state does when material leaves PRISM. */
export const RIGHTS_ACTIONS = ['block', 'override', 'warn', 'allow'] as const;
export type RightsAction = (typeof RIGHTS_ACTIONS)[number];
export const RIGHTS_ACTION_LABELS: Record<RightsAction, string> = {
  block: 'Block', override: 'Block, rights managers can override', warn: 'Warn', allow: 'Allow',
};

/** The states the policy table lists (cleared has no row: it never asks). */
export const RIGHTS_POLICY_STATES = ['withdrawn', 'ended', 'embargoed', 'not_cleared', 'restricted', 'unknown'] as const;
export type RightsPolicyState = (typeof RIGHTS_POLICY_STATES)[number];

const Action = z.enum(RIGHTS_ACTIONS);
/** Per state; withdrawn is fixed to block (a kill order). Defaults: plan section 4. */
export const RightsPolicySchema = z.object({
  withdrawn: z.literal('block').default('block'),
  ended: Action.default('override'),
  embargoed: Action.default('override'),
  not_cleared: Action.default('override'),
  restricted: Action.default('warn'),
  unknown: Action.default('allow'),
});
export type RightsPolicy = z.infer<typeof RightsPolicySchema>;
export const DEFAULT_RIGHTS_POLICY: RightsPolicy = RightsPolicySchema.parse({});

export const DEFAULT_ENDING_SOON_DAYS = 30;
export const DEFAULT_RIGHTS_DIGEST_HOUR = 7;

/** workspace_settings.rights (0106), GET/PUT /api/v1/settings/rights. NULL in the database: these defaults. */
export const RightsSettingsSchema = z.object({
  /** "Use rights management": off until an administrator switches it on. */
  enabled: z.boolean().default(false),
  policy: RightsPolicySchema.default(() => ({ ...DEFAULT_RIGHTS_POLICY })),
  /** Rights ending within this many days are "ending soon" (badge, filter, digest). */
  endingSoonDays: z.number().int().min(1).max(365).default(DEFAULT_ENDING_SOON_DAYS),
  /** "Send the daily email": the daily digest to rights managers; on unless switched off (settings saved before it: on). */
  digestEnabled: z.boolean().default(true),
  /** The hour (workspace time zone) the daily digest is sent. */
  digestHour: z.number().int().min(0).max(23).default(DEFAULT_RIGHTS_DIGEST_HOUR),
  /**
   * The workspace time zone of rights (IANA): Available until ends at the end of its day there, relative dates of
   * watch folders and live sources are counted there, and the digest goes at its digest hour there. PRISM has no
   * other workspace time zone; the default is the date triggers' (DEFAULT_SCHEDULE_TIMEZONE).
   */
  timeZone: z.string().max(64).refine(isTimeZone, 'Not a time zone').default(DEFAULT_SCHEDULE_TIMEZONE),
});
export type RightsSettings = z.infer<typeof RightsSettingsSchema>;
export const RightsSettingsInput = RightsSettingsSchema;
export interface RightsSettingsDTO extends RightsSettings { updatedAt: string | null }

/** Stored settings (null, {} or a partial object) with every key, defaults filled in. */
export function rightsSettingsOf(stored: unknown): RightsSettings {
  const r = RightsSettingsSchema.safeParse(stored && typeof stored === 'object' ? stored : {});
  return r.success ? r.data : RightsSettingsSchema.parse({});
}

/** Preflight severities (production/preflight.ts PreflightProblem.severity; absent: block, as every problem before 1.8.0). */
export const PREFLIGHT_SEVERITIES = ['block', 'override', 'confirm'] as const;
export type PreflightSeverity = (typeof PREFLIGHT_SEVERITIES)[number];

/**
 * What the policy asks for a state: 'block' (it can't be done), 'override' (a rights manager with a reason),
 * 'confirm' (anyone, after ticking the box), or null (nothing to ask). Withdrawn always blocks; cleared never asks.
 */
export function rightsOutcome(state: RightsState, policy: RightsPolicy): PreflightSeverity | null {
  if (state === 'withdrawn') return 'block';
  if (state === 'cleared') return null;
  const action = policy[state];
  return action === 'block' ? 'block' : action === 'override' ? 'override' : action === 'warn' ? 'confirm' : null;
}

// ---------------------------------------------------------------- problems, and the request's acknowledgement

export const RIGHTS_PROBLEM_CODES = {
  withdrawn: 'RIGHTS_WITHDRAWN', ended: 'RIGHTS_ENDED', embargoed: 'RIGHTS_EMBARGOED', not_cleared: 'RIGHTS_NOT_CLEARED',
  restricted: 'RIGHTS_RESTRICTED', unknown: 'RIGHTS_UNKNOWN',
} as const satisfies Record<RightsPolicyState, string>;
export type RightsProblemCode = (typeof RIGHTS_PROBLEM_CODES)[RightsPolicyState];
export const RIGHTS_PROBLEM_CODE_LIST = Object.values(RIGHTS_PROBLEM_CODES) as RightsProblemCode[];
export const isRightsProblemCode = (c: string): c is RightsProblemCode => (RIGHTS_PROBLEM_CODE_LIST as string[]).includes(c);

/** The plain-language reason of each problem, after the asset's title. */
export const RIGHTS_PROBLEM_MESSAGES: Record<RightsPolicyState, string> = {
  withdrawn: 'has been withdrawn and must not be used',
  ended: 'its rights have ended',
  embargoed: 'is under embargo',
  not_cleared: 'its rights are not cleared',
  restricted: 'has rights restrictions',
  unknown: 'has no rights information',
};

/** “Title” has been withdrawn and must not be used / its rights ended on 2026-10-30 / is under embargo until … */
export function rightsProblemMessage(title: string, r: Pick<RightsInfo, 'state' | 'from' | 'until' | 'restrictions'>): string {
  if (r.state === 'cleared') return `“${title}” is cleared`;
  const base = `“${title}”: ${RIGHTS_PROBLEM_MESSAGES[r.state]}`;
  if (r.state === 'ended' && r.until) return `“${title}”: its rights ended on ${r.until}`;
  if (r.state === 'embargoed' && r.from) return `“${title}”: under embargo until ${r.from.replace('T', ' ').replace(/:00Z$/, ' UTC')}`;
  if (r.state === 'restricted' && r.restrictions) return `${base}: ${r.restrictions}`;
  return base;
}

/** The 409 codes of a start that needs more than the request gave (assertPreflight, 1.8.0). */
export const RIGHTS_OVERRIDE_REQUIRED = 'RIGHTS_OVERRIDE_REQUIRED';
export const RIGHTS_CONFIRMATION_REQUIRED = 'RIGHTS_CONFIRMATION_REQUIRED';
/** 403: a change to rights fields or to a watch folder's or live source's rights defaults without rights.manage. */
export const RIGHTS_MANAGE_REQUIRED = 'RIGHTS_MANAGE_REQUIRED';
/** 409: DELETE of a relation PRISM recorded (asset_relations.origin = 'system'). */
export const RELATION_LOCKED = 'RELATION_LOCKED';
export const RELATION_LOCKED_MESSAGE = 'Recorded by PRISM when the asset was made; it can’t be removed';

/**
 * The request's `rights` field on every start that checks rights (exports, Send to edit, share links, manual runs):
 * `confirm` acknowledges warnings ("I've read the rights restrictions"); `override` (rights.manage) goes past blocks
 * the policy lets rights managers override, with the reason stored and audited.
 */
export const RightsAckSchema = z.object({
  confirm: z.literal(true).optional(),
  override: z.object({ reason: z.string().trim().min(1).max(500) }).optional(),
});
export type RightsAck = z.infer<typeof RightsAckSchema>;

// ---------------------------------------------------------------- the Library filter

export const RIGHTS_FILTERS = ['cleared', 'restricted', 'not_cleared', 'withdrawn', 'embargoed', 'ended', 'ending_soon', 'unknown'] as const;
export type RightsFilter = (typeof RIGHTS_FILTERS)[number];
export const RIGHTS_FILTER_LABELS: Record<RightsFilter, string> = {
  cleared: 'Cleared', restricted: 'Restricted', not_cleared: 'Not cleared', withdrawn: 'Withdrawn', embargoed: 'Embargoed', ended: 'Ended',
  ending_soon: 'Ending soon', unknown: 'Unknown',
};
/** The filter's label, "Ends within 30 days" for ending_soon. */
export const rightsFilterLabel = (f: RightsFilter, endingSoonDays: number): string =>
  (f === 'ending_soon' ? `Ends within ${endingSoonDays} day${endingSoonDays === 1 ? '' : 's'}` : RIGHTS_FILTER_LABELS[f]);

// ---------------------------------------------------------------- rights defaults of watch folders and live sources

/** Dates relative to arrival (a watch folder) or to the recording's start (a live source); resolveRightsDates. */
export const RightsDatesSchema = z.object({
  fromAfterHours: z.number().int().min(0).max(24 * 365).optional(),
  untilAfterDays: z.number().int().min(0).max(3650).optional(),
});
export type RightsDates = z.infer<typeof RightsDatesSchema>;

/** A live source's rights (settings.rights): field id → value, as a watch folder's defaults, and the relative dates. */
export const SourceRightsSchema = z.object({
  defaults: z.record(z.string().regex(/^f_[a-z0-9_]+$/), z.unknown()).default({}),
  dates: RightsDatesSchema.default(() => ({})),
});
export type SourceRights = z.infer<typeof SourceRightsSchema>;

/**
 * The rights_from and rights_until values for something that arrived at `arrival` (epoch milliseconds): from is
 * N hours later (a UTC instant), until is the local date N days after the arrival's local date in `tz`. Left out when
 * the folder or source has none.
 */
export function resolveRightsDates(dates: RightsDates | null | undefined, arrival: number, tz: string): { rights_from?: string; rights_until?: string } {
  const out: { rights_from?: string; rights_until?: string } = {};
  if (dates?.fromAfterHours !== undefined) out.rights_from = normalizeDateTime(new Date(arrival + dates.fromAfterHours * HOUR_MS).toISOString())!;
  if (dates?.untilAfterDays !== undefined) out.rights_until = shiftDate(localDate(arrival, tz), dates.untilAfterDays);
  return out;
}

// ---------------------------------------------------------------- the rights report (CSV)

/** One use of a source in a rights report (GET …/rights-report.csv). Ticks are on the programme's frame grid. */
export interface RightsReportRow {
  code: string | null;
  title: string;
  kind: string;
  track: string;
  sourceInTicks: number | null;
  sourceOutTicks: number | null;
  recordInTicks: number | null;
  recordOutTicks: number | null;
  holder: string | null;
  source: string | null;
  reference: string | null;
  /** The status recorded with the use (genealogy rights_snapshot); null for a live sequence report. */
  stateThen: RightsState | null;
  stateNow: RightsState | null;
  from: string | null;
  until: string | null;
  restrictions: string | null;
  credit: string | null;
  /** The reader can't see the source: "Restricted asset", without what identifies it. */
  hidden?: boolean;
}

export const RIGHTS_REPORT_COLUMNS = [
  'Code', 'Title', 'Kind', 'Track', 'Source in', 'Source out', 'Programme in', 'Programme out', 'Duration used', 'Holder', 'Source',
  'Reference', 'Status then', 'Status now', 'Available from', 'Available until', 'Restrictions', 'Credit',
] as const;

/** A CSV cell: quoted when it needs to be, and a leading =, +, - or @ neutralised (spreadsheet formulas). */
export function csvCell(v: string | number | null | undefined): string {
  let s = v === null || v === undefined ? '' : String(v);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** The report as CSV text (a header line, then one line per row, CRLF line ends), timecodes at `frameRate`. */
/**
 * Stream A's addition (1.8.0, additive): GET /api/v1/settings/rights/counts (admin), Administration → Rights' counts,
 * each what the Library's Rights filter of the same key finds now (live assets, not dismissed); `ending_soon` within
 * `endingSoonDays`.
 */
export interface RightsCountsDTO {
  enabled: boolean;
  endingSoonDays: number;
  counts: Record<RightsFilter, number>;
  at: string;
}

export function rightsReportRows(rows: readonly RightsReportRow[], opts: { frameRate: Rational }): string {
  const tc = (t: number | null) => (t === null ? '' : ticksToTimecodeString(Math.max(0, t), opts.frameRate));
  const label = (s: RightsState | null) => (s ? RIGHTS_STATE_LABELS[s] : '');
  const lines = [RIGHTS_REPORT_COLUMNS.map(csvCell).join(',')];
  for (const r of rows) {
    const h = Boolean(r.hidden);
    const used = r.recordInTicks !== null && r.recordOutTicks !== null ? r.recordOutTicks - r.recordInTicks
      : r.sourceInTicks !== null && r.sourceOutTicks !== null ? r.sourceOutTicks - r.sourceInTicks : null;
    lines.push([
      h ? '' : r.code, h ? 'Restricted asset' : r.title, r.kind, r.track, tc(r.sourceInTicks), tc(r.sourceOutTicks), tc(r.recordInTicks), tc(r.recordOutTicks),
      tc(used), h ? '' : r.holder, h ? '' : r.source, h ? '' : r.reference, label(r.stateThen), label(r.stateNow), r.from, r.until,
      h ? '' : r.restrictions, h ? '' : r.credit,
    ].map(csvCell).join(','));
  }
  return lines.join('\r\n') + '\r\n';
}
